// /rf-admin/tenants/:id — テナント詳細(M5 統一 CRUD)
//
// Tabs:
// - 基本情報(編集 / 停止・有効化 / 削除)
// - ユーザー(作成フォーム + テーブル + 編集 / リセットPW / 停止・有効化)
//
// 2026-09-19:
//   - 「招待」→「作成」:RF Admin 直接作成,no invite email
//   - デフォルト password = "StrongPass!2026"(Email の下に表示、admin が user に共有)
//   - 新規 user は status='active'(invited 状態廃止)
//   - Send-Invite / Resend ボタン非表示(backend 接口は残す)
//   - 文言は日本語に統一(技術用語 Email/Role/Password は英語のまま)

import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";
import {
  rfAdminCreateUser,
  rfAdminDeleteTenant,
  rfAdminEditUser,
  rfAdminGetTenant,
  rfAdminListTenantUsers,
  rfAdminResetUserPassword,
  rfAdminResendSetup,
  rfAdminSendSetup,
  rfAdminUpdateTenant,
  type RfRoleCode,
  type RfTenant,
  type RfUser,
} from "../api/admin";
import { Card } from "../../components/ui/Card";
import { Table, type TableColumn } from "../../components/ui/Table";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { PageTitle } from "../../components/ui/PageTitle";

type Tab = "basic" | "users";

const TENANT_STATUS_COLOR: Record<string, "green" | "gray" | "red" | "yellow"> = {
  active: "green",
  suspended: "gray",
  deleted: "red",
};
const TENANT_STATUS_LABEL: Record<string, string> = {
  active: "稼働中",
  suspended: "停止中",
  deleted: "削除済み",
};
const USER_STATUS_COLOR: Record<string, "green" | "gray" | "yellow" | "red"> = {
  active: "green",
  invited: "yellow",
  disabled: "gray",
};
const USER_STATUS_LABEL: Record<string, string> = {
  active: "稼働中",
  invited: "招待中",
  disabled: "停止中",
};

const DEFAULT_USER_PASSWORD = "StrongPass!2026";

// ─────────────────── Modal helpers ───────────────────

function ModalShell({
  title,
  subtitle,
  onClose,
  width = 520,
  children,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  width?: number;
  children: React.ReactNode;
}) {
  return (
    <div className="blog-edit-modal-overlay" role="dialog" aria-modal="true">
      <div className="blog-edit-modal-container" style={{ maxWidth: width }}>
        <div className="blog-edit-modal-header">
          <div>
            <div className="blog-edit-modal-title">{title}</div>
            {subtitle && <div className="rf-dialog-subtitle">{subtitle}</div>}
          </div>
          <button type="button" className="blog-edit-modal-close" onClick={onClose} aria-label="閉じる">
            <X size={16} />
          </button>
        </div>
        <div className="blog-edit-modal-body rf-admin-dialog-body" style={{ display: "block", overflow: "auto", padding: "20px 28px 24px" }}>{children}</div>
      </div>
    </div>
  );
}

// ─────────────────── Edit Tenant Dialog ───────────────────

function RfEditTenantDialog({
  tenant,
  onClose,
  onSaved,
}: {
  tenant: RfTenant;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [contractType, setContractType] = useState(tenant.contract_type);
  const [contractStart, setContractStart] = useState(tenant.contract_start);
  const [contractEnd, setContractEnd] = useState(tenant.contract_end ?? "");
  const [featuresCsv, setFeaturesCsv] = useState(tenant.features.join(", "));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function onSave() {
    setSaving(true);
    setError(null);
    try {
      const features = featuresCsv
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      await rfAdminUpdateTenant(tenant.id, {
        contract_type: contractType,
        contract_start: contractStart || null,
        contract_end: contractEnd || null,
        features,
      } as Partial<RfTenant>);
      onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title="テナント編集" onClose={onClose}>
      <div className="rf-admin-form">
        <div className="field">
          <label className="field-label">会社名</label>
          <input className="input" value={tenant.company_name} disabled />
          <div className="field-hint">会社名は作成後に変更できません</div>
        </div>
        <div className="field">
          <label className="field-label">契約種別</label>
          <select className="select" value={contractType} onChange={(e) => setContractType(e.target.value)}>
            <option value="standard">standard</option>
            <option value="trial">trial</option>
            <option value="partner">partner</option>
          </select>
        </div>
        <div className="field">
          <label className="field-label">契約開始</label>
          <input
            type="date"
            className="input"
            value={contractStart}
            onChange={(e) => setContractStart(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="field-label">契約終了 (空欄で無期限)</label>
          <input
            type="date"
            className="input"
            value={contractEnd}
            onChange={(e) => setContractEnd(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="field-label">利用機能 (カンマ区切り)</label>
          <input
            className="input"
            value={featuresCsv}
            onChange={(e) => setFeaturesCsv(e.target.value)}
            placeholder="mail,chatbot,ga4,gbp"
          />
        </div>
        {error && <div className="login-error">{error}</div>}
        <div className="row" style={{ marginTop: 8, justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={onClose}>
            キャンセル
          </Button>
          <Button variant="primary" onClick={onSave} disabled={saving}>
            {saving ? "保存中..." : "保存する"}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

// ─────────────────── Confirm Sub-Dialog(削除確認用)───────────────────

function ConfirmDialog({
  title,
  description,
  confirmLabel,
  confirmDisabled,
  onConfirm,
  onClose,
}: {
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  confirmDisabled?: boolean;
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
}) {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onClick() {
    setWorking(true);
    setError(null);
    try {
      await onConfirm();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setWorking(false);
    }
  }

  return (
    <ModalShell title={title} onClose={onClose} width={460}>
      <div className="rf-confirm-dialog-body">
        {description}
        {error && <div className="rf-field-error">{error}</div>}
      </div>
      <div className="rf-dialog-footer">
        <button type="button" className="rf-btn-text" onClick={onClose} disabled={working}>
          キャンセル
        </button>
        <div className="rf-dialog-footer-actions">
          <button
            type="button"
            className="rf-btn-danger-outline"
            onClick={onClick}
            disabled={working || confirmDisabled}
          >
            {working ? "処理中..." : confirmLabel}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// ─────────────────── Edit User Dialog ───────────────────

const ROLE_OPTIONS: { value: RfRoleCode; label: string }[] = [
  { value: "system_admin", label: "👑  System Admin" },
  { value: "editor", label: "✏️  Editor" },
  { value: "viewer", label: "👁  Viewer" },
];

// 2026-09-19:"invited" 削除 — RF Admin 直接 active 作成、invite フロー廃止。
const STATUS_OPTIONS: { value: "active" | "disabled"; label: string; dot: string }[] = [
  { value: "active", label: "稼働中", dot: "🟢" },
  { value: "disabled", label: "停止中", dot: "⚪" },
];

function RfEditUserDialog({
  user,
  onClose,
  onSaved,
}: {
  user: RfUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [email, setEmail] = useState(user.email);
  const [emailTouched, setEmailTouched] = useState(false);
  const [roleCode, setRoleCode] = useState<RfRoleCode>(user.role_code ?? "viewer");
  const [status, setStatus] = useState<"active" | "disabled">(
    user.status === "invited" ? "active" : user.status,
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const emailInvalid =
    emailTouched && email.length > 0 && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

  async function onSave() {
    setSaving(true);
    setError(null);
    try {
      await rfAdminEditUser(user.id, {
        email: email !== user.email ? email : undefined,
        role_code: roleCode,
        status,
      });
      onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function onResetPassword() {
    const pw = window.prompt(
      `${user.email} の新しいパスワードを入力してください(12文字以上・大小英字・数字・記号)`,
    );
    if (!pw) return;
    try {
      await rfAdminResetUserPassword(user.id, pw);
      alert("パスワードをリセットしました");
    } catch (e) {
      alert(`リセット失敗: ${(e as Error).message}`);
    }
  }

  // 2026-09-19:非表示(RF Admin 直接作成、招待メール廃止)。
  async function onResendInvite() {
    try {
      await rfAdminResendSetup(user.id);
      alert("招待メールを再送しました");
    } catch (e) {
      alert(`再送失敗: ${(e as Error).message}`);
    }
  }

  async function onDelete() {
    await rfAdminEditUser(user.id, { status: "disabled" });
    onSaved();
    onClose();
  }

  return (
    <>
      <ModalShell
        title="ユーザーを編集"
        subtitle="ユーザー情報・権限・状態を変更できます。"
        onClose={onClose}
        width={660}
      >
        {/* Email */}
        <div className="field">
          <label className="field-label" htmlFor="rf-user-email">
            Email <span className="field-label-required">*</span>
          </label>
          <input
            id="rf-user-email"
            type="email"
            className={`input ${emailInvalid ? "rf-input-error" : ""}`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
            required
          />
          <div className="field-hint">ログインに使用するメールアドレスです。</div>
          {emailInvalid && (
            <div className="rf-field-error">メールアドレスの形式が正しくありません。</div>
          )}
        </div>

        {/* Role */}
        <div className="field">
          <label className="field-label" htmlFor="rf-user-role">
            Role
          </label>
          <select
            id="rf-user-role"
            className="select"
            value={roleCode}
            onChange={(e) => setRoleCode(e.target.value as RfRoleCode)}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        {/* Status */}
        <div className="field">
          <label className="field-label" htmlFor="rf-user-status">
            Status
          </label>
          <select
            id="rf-user-status"
            className="select"
            value={status}
            onChange={(e) => setStatus(e.target.value as "active" | "disabled")}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.dot}  {s.label}
              </option>
            ))}
          </select>
        </div>

        {error && <div className="login-error">{error}</div>}

        {/* Security セクション */}
        <div className="rf-dialog-section">
          <div className="rf-dialog-section-label">Security</div>
          <div className="rf-dialog-security-actions">
            <Button variant="secondary" onClick={onResetPassword}>
              パスワードリセット
            </Button>
            {/* 2026-09-19:非表示(招待メール廃止)。backend 接口は残す。 */}
            {false && (
              <Button variant="secondary" onClick={onResendInvite}>
                招待メール再送
              </Button>
            )}
          </div>
        </div>

        {/* Danger Zone */}
        <div className="rf-dialog-danger-zone">
          <div className="rf-dialog-danger-zone-label">Danger Zone</div>
          <p className="rf-dialog-danger-zone-desc">
            このユーザーを停止状態にします。ログインと API アクセスが無効になり、復元する場合は Status を Active に戻してください。
          </p>
          <button type="button" className="rf-btn-danger-outline" onClick={() => setConfirmDeleteOpen(true)}>
            削除
          </button>
        </div>

        {/* フッター:左 キャンセル / 右 保存する */}
        <div className="rf-dialog-footer">
          <button type="button" className="rf-btn-text" onClick={onClose} disabled={saving}>
            キャンセル
          </button>
          <div className="rf-dialog-footer-actions">
            <Button variant="primary" onClick={onSave} disabled={saving || emailInvalid}>
              {saving ? "保存中..." : "保存する"}
            </Button>
          </div>
        </div>
      </ModalShell>

      {confirmDeleteOpen && (
        <ConfirmDialog
          title="このユーザーを削除しますか?"
          description={
            <>
              <p>
                <strong>{user.email}</strong> を停止状態に変更します。
              </p>
              <p>
                ログイン・API アクセスが無効化されます(token_version が増加し、既存のアクセストークンはすべて失効します)。
              </p>
              <p>この操作は Status を <code>Active</code> に戻すと復元できます。</p>
            </>
          }
          confirmLabel="削除する"
          onConfirm={onDelete}
          onClose={() => setConfirmDeleteOpen(false)}
        />
      )}
    </>
  );
}

// ─────────────────── Main Page ───────────────────

export function RfAdminTenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const rf_tenant_id = Number(id);
  const [tab, setTab] = useState<Tab>("basic");
  const [tenant, setTenant] = useState<RfTenant | null>(null);
  const [users, setUsers] = useState<RfUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editTenantOpen, setEditTenantOpen] = useState(false);
  const [editUser, setEditUser] = useState<RfUser | null>(null);

  function reload() {
    if (!Number.isFinite(rf_tenant_id)) return;
    Promise.all([rfAdminGetTenant(rf_tenant_id), rfAdminListTenantUsers(rf_tenant_id)])
      .then(([t, u]) => {
        setTenant(t);
        setUsers(u);
      })
      .catch((e) => setError(e.message));
  }

  useEffect(reload, [rf_tenant_id]);

  const tabBar = (
    <div className="rf-admin-tabs-inline">
      <button
        type="button"
        className={`rf-admin-tab-inline ${tab === "basic" ? "is-active" : ""}`}
        onClick={() => setTab("basic")}
      >
        基本情報
      </button>
      <button
        type="button"
        className={`rf-admin-tab-inline ${tab === "users" ? "is-active" : ""}`}
        onClick={() => setTab("users")}
      >
        ユーザー ({users.length})
      </button>
    </div>
  );

  const backAction = (
    <Link to="/rf-admin/tenants" className="btn btn-secondary">
      <ArrowLeft size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
      一覧へ戻る
    </Link>
  );

  if (error) {
    return (
      <div className="rf-admin-page">
        <PageTitle title="Tenant" actions={backAction} />
        <Card>
          <div className="login-error">{error}</div>
        </Card>
      </div>
    );
  }
  if (!tenant) {
    return (
      <div className="rf-admin-page">
        <PageTitle title="読み込み中..." actions={backAction} />
        <Card>
          <p>読み込み中...</p>
        </Card>
      </div>
    );
  }

  const userColumns: TableColumn<RfUser>[] = [
    { key: "email", header: "Email", render: (u) => u.email },
    {
      key: "role",
      header: "Role",
      render: (u) => u.role_code ?? "—",
    },
    {
      key: "status",
      header: "状態",
      render: (u) => (
        <Badge color={USER_STATUS_COLOR[u.status] ?? "gray"} dot>
          {USER_STATUS_LABEL[u.status] ?? u.status}
        </Badge>
      ),
    },
    { key: "last_login", header: "最終ログイン", render: (u) => u.last_login_at ?? "—" },
    {
      key: "actions",
      header: "操作",
      align: "right",
      render: (u) => (
        <div className="table-actions" style={{ flexWrap: "wrap", gap: 4 }}>
          <Button variant="ghost" onClick={() => setEditUser(u)}>
            編集
          </Button>
          {/* 2026-09-19:「招待送信」「再送」ボタン非表示(backend 接口は残す)。 */}
          {false && (
            <>
              <Button
                variant="secondary"
                onClick={async () => {
                  if (!confirm(`${u.email} に設定完了メールを送信しますか?`)) return;
                  try {
                    await rfAdminSendSetup(tenant!.id, u.id);
                    alert("送信しました");
                  } catch (e) {
                    alert(`送信失敗: ${(e as Error).message}`);
                  }
                }}
              >
                招待送信
              </Button>
              <Button
                variant="secondary"
                onClick={async () => {
                  try {
                    await rfAdminResendSetup(u.id);
                    alert("再送しました");
                  } catch (e) {
                    alert(`再送失敗: ${(e as Error).message}`);
                  }
                }}
              >
                再送
              </Button>
            </>
          )}
          <Button
            variant="secondary"
            onClick={async () => {
              const pw = window.prompt(`${u.email} の新しいパスワードを入力してください(12文字以上・大小英字・数字・記号)`);
              if (!pw) return;
              try {
                await rfAdminResetUserPassword(u.id, pw);
                alert("パスワードをリセットしました");
              } catch (e) {
                alert(`リセット失敗: ${(e as Error).message}`);
              }
            }}
          >
            リセットPW
          </Button>
          {u.status !== "disabled" ? (
            <Button
              variant="danger"
              onClick={async () => {
                if (!confirm(`${u.email} を停止しますか?`)) return;
                await rfAdminEditUser(u.id, { status: "disabled" });
                reload();
              }}
            >
              停止
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={async () => {
                await rfAdminEditUser(u.id, { status: "active" });
                reload();
              }}
            >
              有効化
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Basic tab actions
  const basicActions = (
    <div style={{ display: "flex", gap: 8 }}>
      <Button variant="secondary" onClick={() => setEditTenantOpen(true)}>
        編集
      </Button>
      <Button
        variant={tenant.status === "active" ? "danger" : "primary"}
        onClick={async () => {
          if (
            !confirm(
              tenant.status === "active"
                ? "このテナントを停止しますか?"
                : "このテナントを有効化しますか?",
            )
          )
            return;
          await rfAdminUpdateTenant(tenant.id, {
            status: tenant.status === "active" ? "suspended" : "active",
          });
          reload();
        }}
      >
        {tenant.status === "active" ? "停止" : "有効化"}
      </Button>
      {tenant.status !== "deleted" && (
        <Button
          variant="danger"
          onClick={async () => {
            const companyName = tenant.company_name;
            const confirmText = window.prompt(
              `このテナントを削除します(ソフト削除)。確認のため会社名 "${companyName}" を入力してください:`,
            );
            if (confirmText !== companyName) return;
            try {
              await rfAdminDeleteTenant(tenant.id);
              navigate("/rf-admin/tenants");
            } catch (e) {
              alert(`削除失敗: ${(e as Error).message}`);
            }
          }}
        >
          削除
        </Button>
      )}
    </div>
  );

  return (
    <div className="rf-admin-page">
      <PageTitle title={tenant.company_name} subtitle={`Tenant #${tenant.id}`} actions={backAction} />
      <div style={{ marginBottom: 16 }}>{tabBar}</div>

      {tab === "basic" && (
        <Card title="基本情報" actions={basicActions}>
          <dl className="rf-admin-dl">
            <dt>ID</dt>
            <dd>{tenant.id}</dd>
            <dt>Postgres tenant_id</dt>
            <dd>
              <code>{tenant.postgres_tenant_id}</code>
            </dd>
            <dt>契約種別</dt>
            <dd>{tenant.contract_type}</dd>
            <dt>契約開始</dt>
            <dd>{tenant.contract_start}</dd>
            <dt>契約終了</dt>
            <dd>{tenant.contract_end ?? "—"}</dd>
            <dt>利用機能</dt>
            <dd>{tenant.features.length === 0 ? "—" : tenant.features.join(", ")}</dd>
            <dt>状態</dt>
            <dd>
              <Badge color={TENANT_STATUS_COLOR[tenant.status] ?? "gray"} dot>
                {TENANT_STATUS_LABEL[tenant.status] ?? tenant.status}
              </Badge>
            </dd>
          </dl>
        </Card>
      )}

      {tab === "users" && (
        <>
          <Card title="ユーザーを作成" subtitle="Email と Role を指定。Password は Email の下に表示されます(変更可、デフォルト StrongPass!2026)。">
            <CreateUserForm tenantId={tenant.id} onCreated={reload} />
          </Card>
          <div className="mb-18" />
          <Card title="既存ユーザー" subtitle={`${users.length} 件`}>
            <Table<RfUser>
              columns={userColumns}
              data={users}
              rowKey={(u) => String(u.id)}
              emptyText="まだユーザーがいません。"
            />
          </Card>
        </>
      )}

      {editTenantOpen && (
        <RfEditTenantDialog
          tenant={tenant}
          onClose={() => setEditTenantOpen(false)}
          onSaved={reload}
        />
      )}
      {editUser && (
        <RfEditUserDialog
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={reload}
        />
      )}
    </div>
  );
}

// ─────────────────── Create User Form ───────────────────

function CreateUserForm({
  tenantId,
  onCreated,
}: {
  tenantId: number;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(DEFAULT_USER_PASSWORD);
  const [roleCode, setRoleCode] = useState<RfRoleCode>("viewer");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [createdInfo, setCreatedInfo] = useState<{ email: string; password: string } | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await rfAdminCreateUser(tenantId, {
        email,
        role_code: roleCode,
        password,
      });
      setEmail("");
      setPassword(DEFAULT_USER_PASSWORD);
      setCreatedInfo({ email: result.email, password: result.password });
      onCreated();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="rf-admin-form">
      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 180px auto", gap: 12, alignItems: "end" }}>
        {/* Email */}
        <div className="field" style={{ marginBottom: 0 }}>
          <label className="field-label">
            Email <span className="field-label-required">*</span>
          </label>
          <input
            type="email"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        {/* Password — Email の下(Email と同列の Password 列は廃止) */}
        <div className="field" style={{ marginBottom: 0, gridColumn: "1 / 2" }}>
          <label className="field-label">Password</label>
          <input
            type="text"
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={12}
            required
          />
          <div className="field-hint">デフォルト: {DEFAULT_USER_PASSWORD}。作成したユーザーにお伝えください。</div>
        </div>
        {/* Role */}
        <div className="field" style={{ marginBottom: 0, gridColumn: "2 / 3" }}>
          <label className="field-label">Role</label>
          <select
            className="select"
            value={roleCode}
            onChange={(e) => setRoleCode(e.target.value as RfRoleCode)}
          >
            <option value="viewer">viewer</option>
            <option value="editor">editor</option>
            <option value="system_admin">system_admin</option>
          </select>
        </div>
        <Button type="submit" variant="primary" disabled={submitting} style={{ gridColumn: "3 / 4" }}>
          {submitting ? "作成中..." : "作成する"}
        </Button>
      </div>
      {error && <div className="login-error" style={{ marginTop: 10 }}>{error}</div>}
      {createdInfo && (
        <div
          style={{
            marginTop: 12,
            padding: "12px 14px",
            background: "rgba(34, 197, 94, 0.10)",
            border: "1px solid rgba(34, 197, 94, 0.35)",
            borderRadius: 6,
            color: "#166534",
            fontSize: 13,
            lineHeight: 1.55,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>ユーザーを作成しました。</div>
          <div>
            Email: <code style={{ background: "rgba(0,0,0,0.04)", padding: "1px 4px", borderRadius: 3 }}>{createdInfo.email}</code>
          </div>
          <div>
            Password:{" "}
            <code
              style={{
                background: "rgba(0,0,0,0.04)",
                padding: "2px 6px",
                borderRadius: 3,
                userSelect: "all",
                fontWeight: 600,
              }}
            >
              {createdInfo.password}
            </code>
          </div>
          <div style={{ marginTop: 6, opacity: 0.85 }}>
            上記 Password をコピーしてユーザーにお伝えください。再表示はできません。
          </div>
        </div>
      )}
    </form>
  );
}

